import { Button, Col, Popconfirm, Row, Space, Typography } from 'antd';
import {
  OrderClaimData,
  OrderClaimPrintoutData,
  OrderEnum,
  PrintType,
} from '../../../types';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import {
  getDataWithAuthToken,
  postDataWithAuthToken,
} from '../../../utils/axiosRequest';
import { alertMessage } from '../../../utils/alertMessage';
import { useTab } from '../../../hooks/useTab';
import { dashboardRoute } from '../../../constants/pathname';
import PrintModal from '../../PrintModal';
import { hasPermission } from '../../../utils/hasPermission';
import { actionPermissions } from '../../../constants/actionPermissions';
import ClaimModal from '../ClaimModal';
import { REVIEW_STATUS_DEFAULT } from '../../../constants/generalConstants';
import { BLUE1, HIGHLIGHT_ORANGE, WHITE } from '../../../constants/color';
import getDashboardStyle from '../../../utils/getDashboardStyle';

type ClaimActionProps = {
  orderClaimDetail: OrderClaimData;
  onRefresh: () => void;
  enums?: OrderEnum;
};

const ClaimAction = ({
  orderClaimDetail,
  onRefresh,
  enums,
}: ClaimActionProps) => {
  const { t } = useTranslation();
  const { removeTab } = useTab();
  const [modalVisible, setModalVisible] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [printModalVisible, setPrintModalVisible] = useState(false);
  const [printoutData, setPrintoutData] = useState<PrintType | undefined>(
    undefined
  );

  const getPrintoutContents = () => {
    setModalLoading(true);
    getDataWithAuthToken('claim/print', {
      params: {
        claimIds: [orderClaimDetail.id].toString(),
      },
    }).then((response) => {
      if (response && response.goodStatus) {
        let responseData: OrderClaimPrintoutData[] = response.data.list;
        if (responseData.length > 0) {
          setPrintoutData({ orderClaimData: responseData[0], userId: orderClaimDetail.user?.userId , claimIds: [orderClaimDetail.id]});
          setModalLoading(false);
          setPrintModalVisible(true);
        } else {
          setModalLoading(false);
          alertMessage(
            'error',
            response?.msg || t('general.noResponse'),
            response?.data || undefined
          );
        }
      } else {
        setModalLoading(false);
        alertMessage(
          'error',
          response?.msg || t('general.noResponse'),
          response?.data || undefined
        );
      }
    });
  };

  const handleClaim = (type: 'trash' | 'restore' | 'delete') => {
    postDataWithAuthToken(`claim/${type}`, {
      claimId: orderClaimDetail.id,
    })
      .then((response) => {
        if (response && response.goodStatus) {
          const message =
            type === 'trash'
              ? t('order.alerts.orderClaimArchived')
              : type === 'restore'
              ? t('order.alerts.orderClaimRestored')
              : t('order.alerts.orderClaimDeleted');
          alertMessage('success', message);

          if (type === 'delete') {
            // close tab
            removeTab(
              `${dashboardRoute.order.orderClaimDetail}?claim_id=${orderClaimDetail.id}`
            );
          } else {
            onRefresh();
          }
        } else {
          alertMessage(
            'error',
            response?.msg || t('general.noResponse'),
            response?.data || undefined
          );
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const handleUseClaim = (isUsed: boolean) => {
    postDataWithAuthToken(`claim/batch/edit`, {
      claimIds: [orderClaimDetail.id],
      isUsed: isUsed,
    })
      .then((response) => {
        if (response && response.goodStatus) {
          alertMessage('success', t('order.alerts.orderClaimUpdateUseStatus'));
          onRefresh();
        } else {
          alertMessage(
            'error',
            response?.msg || t('general.noResponse'),
            response?.data || undefined
          );
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  return (
    <>
      <Row gutter={[16, 24]}>
        <Col span={24}>
          <Typography.Text strong style={{ fontSize: 16 }}>
            {t('order.claimListColumns.actions')}
          </Typography.Text>
        </Col>

        <Col span={24}>
          <Space wrap>
            
            {!orderClaimDetail.isDelete && orderClaimDetail.reviewStatus.code === REVIEW_STATUS_DEFAULT && (
              <Button
                type="primary"
                onClick={() => setModalVisible(true)}
                
                disabled={
                  !hasPermission(actionPermissions.orderGroup.claimManage)
                }
              >
                {t('actionsColumn.review')}
              </Button>
            )}
            <Button
              style={{
                  backgroundColor: HIGHLIGHT_ORANGE,
                  color: WHITE,
                }}
              onClick={() => {
                getPrintoutContents();
              }}
            >
              {t('actionsColumn.print')}
            </Button>
            {/***** isUsed 只有批发端的 CREDIT_MEMO 才使用这个状态 */}
            {!orderClaimDetail.isDelete &&
              getDashboardStyle().isWholeSaleAppType &&
              orderClaimDetail.claimType?.code === 'CREDIT_MEMO' && (
              <Button
                onClick={() =>
                  handleUseClaim(orderClaimDetail.isUsed ? false : true)
                }
                disabled={
                  !hasPermission(actionPermissions.orderGroup.claimManage)
                }
                style={!orderClaimDetail.isUsed ? { backgroundColor: BLUE1, color: WHITE } : {}}
              >
                {orderClaimDetail.isUsed
                  ? t('order.claimListColumns.setUnused')
                  : t('order.claimListColumns.setIsUsed')}
              </Button>
            )}
            {orderClaimDetail.isDelete && (
              <Popconfirm
                title={t('actionsColumn.restoreWarning')}
                onConfirm={() => {
                  handleClaim('restore');
                }}
                okText={t('actionsColumn.confirmation.yes')}
                cancelText={t('actionsColumn.confirmation.no')}
                disabled={
                  !hasPermission(actionPermissions.orderGroup.claimManage)
                }
              >
                <Button>{t('actionsColumn.restore')}</Button>
              </Popconfirm>
            )}
            <Popconfirm
              title={
                orderClaimDetail.isDelete
                  ? t('actionsColumn.deleteWarning')
                  : t('actionsColumn.archiveWarning')
              }
              onConfirm={() => {
                handleClaim(orderClaimDetail.isDelete ? 'delete' : 'trash');
              }}
              okText={t('actionsColumn.confirmation.yes')}
              cancelText={t('actionsColumn.confirmation.no')}
              disabled={
                !hasPermission(actionPermissions.orderGroup.claimManage)
              }
            >
              <Button danger type='link'>
                {orderClaimDetail.isDelete
                  ? t('actionsColumn.delete')
                  : t('actionsColumn.archive')}
              </Button>
            </Popconfirm>
          </Space>
        </Col>
      </Row>
      <PrintModal
        modalLoading={modalLoading}
        mode="orderClaim"
        data={printoutData}
        setData={setPrintoutData}
        visible={printModalVisible}
        setVisible={setPrintModalVisible}
      />
      <ClaimModal
        selectedRefund={orderClaimDetail}
        visible={modalVisible}
        setVisible={setModalVisible}
        refresh={onRefresh}
        enums={enums}
      />
    </>
  );
};

export default ClaimAction;
